// Defective products registry tab.
// COMPLETELY INDEPENDENT from real stock, Shopify stock, supply, bundles.
// Pure internal headcount + value tracking of defective items in warehouse.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertOctagon, Plus, Trash2, Search, Minus, ShieldAlert, Coins, Boxes, Pencil, X, Check,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmtMoney = (v) => (v == null ? "—" : Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtInt = (v) => (v == null ? "—" : Number(v).toLocaleString("ro-RO"));

export default function DefectiveTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/defective");
      setData(r.data);
    } catch (e) {
      toast.error("Eroare la încărcare");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = (search || "").trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((r) =>
      (r.title || "").toLowerCase().includes(q) ||
      (r.sku || "").toLowerCase().includes(q) ||
      (r.variant_title || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  if (loading) return <div className="h-64 rounded-xl bg-card/40 border border-border animate-pulse" />;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="defective-tab">
      {/* Disclaimer banner */}
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 flex items-start gap-3 text-xs text-amber-200">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold">Registru intern</span> — produsele defecte nu afectează stocul real, stocul Shopify, calculele de aprovizionare, bundle-urile sau analytics-ul de stoc vandabil. Doar evidență cantitativă internă.
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard label="SKU-uri defecte" value={fmtInt(data.totals.skus)} icon={AlertOctagon} color="text-rose-500" testId="defective-kpi-skus" />
        <KpiCard label="Bucăți defecte (total)" value={fmtInt(data.totals.qty)} icon={Boxes} color="text-amber-500" testId="defective-kpi-qty" />
        <KpiCard label="Valoare stoc defect (cost)" value={fmtMoney(data.totals.value) + " RON"} icon={Coins} color="text-emerald-500" testId="defective-kpi-value" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Caută după produs, SKU, variantă..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
            data-testid="defective-search"
          />
        </div>
        <AddDefectiveDialog onAdded={load} />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-muted-foreground border-b border-border bg-secondary/30">
              <tr>
                <th className="text-left py-2 px-4">Produs</th>
                <th className="text-left">SKU</th>
                <th className="text-right">Cant. defectă</th>
                <th className="text-right">Cost / unit</th>
                <th className="text-right">Valoare stoc defect</th>
                <th className="text-right pr-4">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted-foreground py-12 text-sm">
                    {search ? "Niciun produs nu corespunde căutării." : 'Niciun produs defect înregistrat. Folosește butonul "Adaugă produs defect" pentru a începe.'}
                  </td>
                </tr>
              ) : filtered.map((r) => <Row key={r.variant_id} row={r} onChanged={load} />)}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-secondary/30">
                <tr>
                  <td colSpan="2" className="px-4 py-2.5 text-xs uppercase tracking-wider text-muted-foreground">
                    Total ({filtered.length} {filtered.length === 1 ? "SKU" : "SKU-uri"})
                  </td>
                  <td className="text-right tabular-nums font-semibold">
                    {fmtInt(filtered.reduce((a, b) => a + b.qty_defective, 0))}
                  </td>
                  <td />
                  <td className="text-right tabular-nums font-semibold text-emerald-500" data-testid="defective-table-total-value">
                    {fmtMoney(filtered.reduce((a, b) => a + b.value, 0))} RON
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color = "text-primary", testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-4" data-testid={testId}>
      <div className={`h-10 w-10 rounded-lg bg-secondary/40 flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <div className="text-xl font-display font-semibold tabular-nums tracking-tight">{value}</div>
      </div>
    </div>
  );
}

function Row({ row, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(row.qty_defective));
  const [busy, setBusy] = useState(false);

  const adjust = async (delta) => {
    setBusy(true);
    try {
      await api.post(`/defective/${row.variant_id}/adjust`, { delta });
      await onChanged();
    } catch (e) { toast.error("Eroare"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) { toast.error("Cantitate invalidă"); return; }
    setBusy(true);
    try {
      await api.put(`/defective/${row.variant_id}`, { qty: n });
      setEditing(false);
      await onChanged();
    } catch (e) { toast.error("Eroare"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/defective/${row.variant_id}`);
      toast.success("SKU eliminat din registru");
      await onChanged();
    } catch (e) { toast.error("Eroare"); }
    finally { setBusy(false); }
  };

  return (
    <tr className="border-b border-border/50 hover:bg-secondary/20 transition-colors" data-testid={`defective-row-${row.variant_id}`}>
      <td className="py-2.5 px-4">
        <div className="flex items-center gap-3 max-w-md">
          {row.image ? (
            <img src={row.image} alt="" className="h-10 w-10 rounded-md object-cover bg-secondary/40" />
          ) : (
            <div className="h-10 w-10 rounded-md bg-secondary/40" />
          )}
          <div className="min-w-0">
            <div className="font-medium truncate" title={row.title}>{row.title}</div>
            {row.variant_title && <div className="text-[11px] text-muted-foreground truncate">{row.variant_title}</div>}
          </div>
        </div>
      </td>
      <td className="font-mono text-xs text-muted-foreground">{row.sku || "—"}</td>
      <td className="text-right">
        {editing ? (
          <div className="inline-flex items-center gap-1">
            <Input
              type="number" min="0" step="1"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className="h-7 w-16 text-right tabular-nums"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(String(row.qty_defective)); setEditing(false); } }}
              data-testid={`defective-qty-input-${row.variant_id}`}
            />
            <button onClick={save} disabled={busy} className="text-emerald-500 hover:text-emerald-400 p-1" data-testid={`defective-qty-save-${row.variant_id}`}>
              <Check className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => { setVal(String(row.qty_defective)); setEditing(false); }} className="text-muted-foreground hover:text-foreground p-1">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="inline-flex items-center gap-1">
            <button
              onClick={() => adjust(-1)}
              disabled={busy || row.qty_defective <= 0}
              className="h-6 w-6 rounded border border-border hover:border-rose-500/40 hover:bg-rose-500/10 text-rose-500 flex items-center justify-center disabled:opacity-30"
              title="Scade o bucată"
              data-testid={`defective-minus-${row.variant_id}`}
            >
              <Minus className="h-3 w-3" />
            </button>
            <button
              onClick={() => setEditing(true)}
              className="px-2 py-0.5 rounded font-semibold tabular-nums hover:bg-secondary/40 group flex items-center gap-1"
              title="Click pentru editare cantitate"
              data-testid={`defective-qty-${row.variant_id}`}
            >
              {fmtInt(row.qty_defective)}
              <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-50 transition-opacity" />
            </button>
            <button
              onClick={() => adjust(1)}
              disabled={busy}
              className="h-6 w-6 rounded border border-border hover:border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-500 flex items-center justify-center"
              title="Adaugă o bucată"
              data-testid={`defective-plus-${row.variant_id}`}
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        )}
      </td>
      <td className="text-right tabular-nums text-muted-foreground">{fmtMoney(row.cost)} RON</td>
      <td className="text-right tabular-nums font-semibold text-emerald-500">{fmtMoney(row.value)} RON</td>
      <td className="text-right pr-4">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="text-rose-500 hover:text-rose-400 p-1.5 rounded hover:bg-rose-500/10"
              data-testid={`defective-delete-${row.variant_id}`}
              title="Elimină SKU din registru"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Elimini SKU din registru?</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{row.title}</strong> ({row.sku}) — cantitatea <strong>{row.qty_defective}</strong> va fi eliminată din registrul de produse defecte. Operația va fi păstrată în istoricul auditului.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anulează</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid={`defective-delete-confirm-${row.variant_id}`}>
                Elimină
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  );
}

function AddDefectiveDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [variants, setVariants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/defective/search-variants", { params: { q: search, limit: 30 } });
        setVariants(r.data || []);
      } catch (e) {}
    }, 200);
    return () => clearTimeout(t);
  }, [open, search]);

  const reset = () => { setSelected(null); setQty("1"); setSearch(""); setVariants([]); };

  const save = async () => {
    if (!selected) { toast.error("Selectează un produs"); return; }
    const n = parseInt(qty, 10);
    if (isNaN(n) || n < 1) { toast.error("Cantitate invalidă"); return; }
    setBusy(true);
    try {
      await api.post("/defective", { variant_id: selected.variant_id, qty: n });
      toast.success("Adăugat în registru");
      reset();
      setOpen(false);
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="defective-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Adaugă produs defect
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-3 border-b border-border shrink-0">
          <DialogTitle>Adaugă produs defect</DialogTitle>
          <DialogDescription>
            Selectează produsul (după nume sau SKU) și introdu cantitatea defectă. Nu afectează stocurile reale.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 pt-4 min-h-0">
          {!selected ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  autoFocus
                  placeholder="Caută după nume produs sau SKU..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="defective-add-search"
                />
              </div>
              <div className="rounded-lg border border-border overflow-hidden">
                {variants.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">Tastați pentru a căuta produse</div>
                ) : (
                  <ul className="divide-y divide-border">
                    {variants.map((v) => (
                      <li key={v.variant_id}>
                        <button
                          type="button"
                          onClick={() => setSelected(v)}
                          className="w-full text-left px-3 py-2 hover:bg-secondary/40 flex items-center gap-3"
                          data-testid={`defective-add-pick-${v.variant_id}`}
                        >
                          {v.image ? <img src={v.image} alt="" className="h-9 w-9 rounded-md object-cover bg-secondary/40 shrink-0" /> : <div className="h-9 w-9 rounded-md bg-secondary/40 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{v.title}</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {v.variant_title && <span>{v.variant_title} · </span>}
                              <span className="font-mono">{v.sku || "—"}</span> · {fmtMoney(v.cost)} RON / unit
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border">
                {selected.image ? <img src={selected.image} alt="" className="h-12 w-12 rounded-md object-cover shrink-0" /> : <div className="h-12 w-12 rounded-md bg-secondary/40 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{selected.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {selected.variant_title && <span>{selected.variant_title} · </span>}
                    <span className="font-mono">{selected.sku || "—"}</span> · {fmtMoney(selected.cost)} RON / unit
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Schimbă</Button>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Cantitate defectă</label>
                <Input
                  type="number" min="1" step="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                  data-testid="defective-add-qty"
                  autoFocus
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Valoare estimată: <span className="font-semibold text-emerald-500">{fmtMoney((parseInt(qty || "0", 10) || 0) * selected.cost)} RON</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {selected && (
          <div className="p-4 border-t border-border bg-card/60 shrink-0">
            <Button onClick={save} disabled={busy} className="w-full" data-testid="defective-add-save">
              {busy ? "Salvare..." : "Adaugă în registru"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
